import { RotateCcw, Star } from "lucide-react";
import { NumInput } from "@/components/NumInput";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { brl, pct, channelPct, contribution, pricePassingFees } from "@/lib/api";

const Line = ({ l, v, strong }) => <div className="flex justify-between gap-3"><span className="text-muted-foreground">{l}</span><span className={`num ${strong ? "font-semibold" : ""}`}>{v}</span></div>;

// Card de um canal: Preço de venda final + Margem líquida real. Sugerido = preço padrão ÷ (1 − taxa do canal) (repasse da taxa).
export const ChannelPriceCard = ({ ch, cost, basePrice, draft, onChange, onInherit }) => {
  const fee = channelPct(ch);
  const inherits = draft == null && !ch.is_default;
  const price = draft != null ? Number(draft) || 0 : basePrice;
  const { cm, cmPct, varTotal } = contribution(price, cost, ch);
  const marginCls = cm == null ? "text-muted-foreground" : cm < 0 ? "text-rose-500" : "text-foreground";
  const sugg = pricePassingFees(basePrice, ch);
  const showSugg = !ch.is_default && basePrice > 0;
  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid={`channel-card-${ch.id}`}>
      <div className="min-w-0">
        <p className="font-display font-semibold flex items-center gap-1.5 truncate">{ch.is_default && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />}{ch.name}</p>
        <p className="text-[11px] text-muted-foreground num">taxas {pct(fee, 2)}{ch.is_default ? " · canal padrão" : inherits ? " · herda o preço padrão" : " · preço próprio"}</p>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Preço de venda final</p>
        <div className="flex items-center gap-2">
          <NumInput value={draft != null ? draft : basePrice ? basePrice.toFixed(2) : ""} onChange={(e) => onChange(e.target.value)} placeholder="0,00" className={`num text-lg font-bold h-11 ${inherits ? "text-muted-foreground" : ""}`} data-testid={`channel-price-${ch.id}`} />
          {!ch.is_default && draft != null && <button type="button" title="Voltar a herdar o preço padrão" onClick={onInherit} className="shrink-0 h-9 w-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary" data-testid={`channel-inherit-${ch.id}`}><RotateCcw className="w-4 h-4" /></button>}
        </div>
      </div>

      <div className="flex items-end justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Margem líquida real</p>
          <p className={`num text-2xl font-extrabold ${marginCls}`} data-testid={`channel-margin-${ch.id}`}>{pct(cmPct)}</p>
        </div>
        <p className={`num text-sm font-semibold ${marginCls}`} data-testid={`channel-cm-${ch.id}`}>{cm == null ? "—" : `${brl(cm)} / un`}</p>
      </div>

      {showSugg && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <button type="button" onClick={() => onChange(sugg.toFixed(2))} title={`${brl(basePrice)} ÷ (1 − ${pct(fee, 2)}) — repassa a taxa do canal mantendo o retorno do preço padrão`} className="rounded-full px-2.5 py-1 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 num" data-testid={`channel-use-suggested-${ch.id}`}>Sugerido {brl(sugg)}</button>
          <span className="text-muted-foreground num">= {brl(basePrice)} ÷ (1 − {pct(fee, 2)})</span>
        </div>
      )}

      <Accordion type="single" collapsible>
        <AccordionItem value="calc" className="border-0">
          <AccordionTrigger className="py-1.5 text-xs text-muted-foreground hover:no-underline" data-testid={`channel-details-${ch.id}`}>Ver cálculo e fórmulas</AccordionTrigger>
          <AccordionContent className="text-xs space-y-1.5 pb-1" data-testid={`channel-details-body-${ch.id}`}>
            <Line l="Custo real (ficha)" v={brl(cost)} />
            <Line l={`Taxas do canal (${pct(fee, 2)} sobre o preço)`} v={price ? brl(varTotal) : "—"} />
            <Line l="Retorno líquido (preço − taxas)" v={price ? brl(price - varTotal) : "—"} strong />
            <Line l="Margem de contribuição" v={cm == null ? "—" : `${brl(cm)} · ${pct(cmPct)}`} strong />
            <Line l="CMV" v={price ? pct((cost / price) * 100) : "—"} />
            <div className="border-t border-border pt-1.5 space-y-1 text-muted-foreground num">
              {showSugg && <p>Sugerido = {brl(basePrice)} ÷ (1 − {pct(fee, 2)}) = <span className="text-foreground">{brl(sugg)}</span> (repassa a taxa; mesmo retorno líquido do preço padrão)</p>}
              <p>Margem líquida real = (preço − custo − taxas) ÷ preço</p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
